/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2026 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <div class="drugstore-form-container">
    <div>
      <div class="card shadow-lg border-0">
        <div class="card-header bg-white border-bottom-0 pb-0 pt-4">
          <div class="d-flex justify-content-between align-items-center mb-3 px-2">
            <div>
              <h4 class="card-title text-primary mb-1">
                <i class="bx bx-store-alt me-2"></i>Edycja Apteki
              </h4>
              <p class="text-muted small mb-0" v-if="drugstore.id">ID: {{ drugstore.id }}</p>
              <p class="text-muted small mb-0" v-else>Tworzenie nowej apteki</p>
            </div>
          </div>

          <ul class="nav nav-tabs card-header-tabs mt-2">
            <li class="nav-item">
              <a
                  class="nav-link fw-bold"
                  :class="{ active: tabActive === 0 }"
                  href="#"
                  @click.prevent="loadTab(0)"
              >
                <i class="bx bx-info-circle me-1"></i> Dane podstawowe
              </a>
            </li>
<!--            Temporarily disabled API tab, unused in app-->
<!--            <li class="nav-item" v-if="!drugstore.current">-->
<!--              <a-->
<!--                  class="nav-link fw-bold"-->
<!--                  :class="{ active: tabActive === 1 }"-->
<!--                  href="#"-->
<!--                  @click.prevent="loadTab(1)"-->
<!--              >-->
<!--                <i class="bx bx-code-block me-1"></i> Konfiguracja API-->
<!--              </a>-->
<!--            </li>-->
            <li class="nav-item">
              <a
                  class="nav-link fw-bold"
                  :class="{ active: tabActive === 2 }"
                  href="#"
                  @click.prevent="loadTab(2)"
              >
                <i class="bx bx-building me-1"></i> Dane Firmy
              </a>
            </li>
            <li class="nav-item">
              <a
                  class="nav-link fw-bold"
                  :class="{ active: tabActive === 3 }"
                  href="#"
                  @click.prevent="loadTab(3)"
              >
                <i class="bx bx-list-check me-1"></i> Referencje / Aliasy
              </a>
            </li>
          </ul>
        </div>

        <div class="card-body p-4">

          <div v-show="tabActive === 0" class="animate-fade-in">
            <h6 class="text-uppercase text-muted fw-bold mb-3 fs-12">Identyfikacja i Adres Placówki</h6>

            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <label class="form-label fw-semibold">Numer licencji Kamsoft</label>
                <div class="input-group">
                  <span class="input-group-text bg-light"><i class="bx bx-id-card"></i></span>
                  <input
                      v-model="drugstore.drugstoreClientId"
                      type="text"
                      :class="drugstoreNumberInputClass"
                      class="form-control"
                      placeholder="000000"
                      :disabled="editedDrugstore != null && editedDrugstore.id != null"
                  />
                  <div class="invalid-feedback">
                    Numer licencji składa się dokładnie z 6 cyfr.
                  </div>
                </div>
              </div>
              <div class="col-md-8">
                <label class="form-label fw-semibold">Nazwa Apteki</label>
                <div class="input-group">
                  <span class="input-group-text bg-light"><i class="bx bx-building-house"></i></span>
                  <input
                      v-model="drugstore.name"
                      type="text"
                      :class="nameInputClass"
                      class="form-control"
                      placeholder="Wpisz pełną nazwę apteki"
                  />
                  <div class="invalid-feedback">
                    Nazwa apteki nie może być pusta.
                  </div>
                </div>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label fw-semibold">NIP</label>
                <div class="input-group">
                  <span class="input-group-text bg-light"><i class="bx bx-hash"></i></span>
                  <input
                      v-model="drugstore.nip"
                      type="text"
                      :class="nipInputClass"
                      class="form-control"
                      placeholder="0000000000"
                  />
                  <div class="invalid-feedback">
                    Nieprawidłowy format NIP.
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">E-mail</label>
                <div class="input-group">
                  <span class="input-group-text bg-light"><i class="bx bx-envelope"></i></span>
                  <input
                      v-model="drugstore.email"
                      type="email"
                      :class="emailInputClass"
                      class="form-control"
                      placeholder="apteka@przyklad.pl"
                  />
                  <div class="invalid-feedback">
                    Wpisz poprawny adres e-mail.
                  </div>
                </div>
              </div>
            </div>

            <hr class="border-light my-4">

            <h6 class="text-uppercase text-muted fw-bold mb-3 fs-12">Adres fizyczny apteki</h6>
            <div class="row g-3 mb-3">
              <div class="col-md-5">
                <label class="form-label fw-semibold">Ulica i numer</label>
                <input
                    v-model="drugstore.address"
                    type="text"
                    :class="streetInputClass"
                    class="form-control"
                    placeholder="np. Długa 15/4"
                />
                <div class="invalid-feedback">Pole wymagane.</div>
              </div>
              <div class="col-md-3">
                <label class="form-label fw-semibold">Kod pocztowy</label>
                <input
                    v-model="drugstore.zipcode"
                    type="text"
                    :class="zipcodeInputClass"
                    class="form-control"
                    placeholder="00-000"
                />
                <div class="invalid-feedback">Pole wymagane.</div>
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Miasto</label>
                <input
                    v-model="drugstore.city"
                    type="text"
                    :class="cityInputClass"
                    class="form-control"
                    placeholder="np. Warszawa"
                />
                <div class="invalid-feedback">Pole wymagane.</div>
              </div>
            </div>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-semibold">Telefon kontaktowy</label>
                <div class="input-group">
                  <span class="input-group-text bg-light"><i class="bx bx-phone"></i></span>
                  <input
                      v-model="drugstore.phoneNumber"
                      type="text"
                      class="form-control"
                      placeholder="+48 000 000 000"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-show="tabActive === 1" class="animate-fade-in" v-if="!drugstore.current">
            <h6 class="text-uppercase text-muted fw-bold mb-3 fs-12">Połączenie z systemem zewnętrznym (KS-Apteka)</h6>
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label fw-semibold">Adres hosta API (Base URL)</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">https://</span>
                  <input
                      v-model="drugstore.apiBaseUrl"
                      type="text"
                      :class="apiHostInputClass"
                      class="form-control"
                      placeholder="api.example.com:8080"
                  />
                  <div class="invalid-feedback">Wymagany adres hosta.</div>
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Login API</label>
                <input
                    v-model="drugstore.apiLogin"
                    type="text"
                    :class="apiLoginInputClass"
                    class="form-control"
                    placeholder="Użytkownik API"
                />
                <div class="invalid-feedback">Pole wymagane.</div>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Hasło API</label>
                <div class="input-group">
                  <span class="input-group-text bg-light"><i class="bx bx-key"></i></span>
                  <input
                      v-model="drugstore.apiPassword"
                      type="password"
                      :class="apiPasswordInputClass"
                      class="form-control"
                      placeholder="****************"
                      autocomplete="new-password"
                  />
                  <div class="invalid-feedback">Pole wymagane.</div>
                </div>
              </div>
            </div>
          </div>

          <div v-show="tabActive === 2" class="animate-fade-in">
            <h6 class="text-uppercase text-muted fw-bold mb-3 fs-12">Dane Właściciela / Do faktury</h6>
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label fw-semibold">Pełna nazwa firmy</label>
                <input
                    v-model="drugstore.companyName"
                    type="text"
                    :class="companyNameInputClass"
                    class="form-control"
                    placeholder="Nazwa podmiotu gospodarczego"
                />
                <div class="invalid-feedback">Nazwa firmy jest wymagana.</div>
              </div>

              <div class="col-12">
                <label class="form-label fw-semibold">Adres siedziby firmy</label>
                <textarea
                    v-model="drugstore.companyAddress"
                    class="form-control"
                    :class="companyAddressInputClass"
                    rows="3"
                    placeholder="Ulica, numer, kod pocztowy, miasto"
                ></textarea>
                <div class="invalid-feedback">Adres firmy jest wymagany.</div>
              </div>

              <div class="col-12"><hr class="my-2 border-light"></div>

              <div class="col-md-8">
                <label class="form-label fw-semibold">Numer konta bankowego</label>
                <div class="input-group">
                  <span class="input-group-text bg-light"><i class="bx bx-credit-card"></i></span>
                  <input
                      v-model="drugstore.accountNumber"
                      type="text"
                      :class="companyAccountInputClass"
                      class="form-control"
                      placeholder="PL 00 0000 0000 0000 0000 0000 0000"
                  />
                  <div class="invalid-feedback">Numer konta jest wymagany.</div>
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Nazwa Banku</label>
                <input
                    v-model="drugstore.bankName"
                    type="text"
                    :class="companyBankInputClass"
                    class="form-control"
                    placeholder="np. mBank"
                />
                <div class="invalid-feedback">Nazwa banku jest wymagana.</div>
              </div>
            </div>
          </div>

          <div v-show="tabActive === 3" class="animate-fade-in">
            <h6 class="text-uppercase text-muted fw-bold mb-3 fs-12">Powiązania systemowe</h6>
            <div class="row">
              <div class="col-12">
                <MutableScrollableListView2
                    ref="referencesList"
                    :key="drugstore.references.length"
                    :listContent="drugstore.references"
                    elementInputPlaceholder="Dodaj ID referencyjne/alias"
                    @onAddElement="addReference($event)"
                    @onRemoveElement="removeReference($event)"
                />
              </div>
            </div>
          </div>

        </div> <div class="card-footer bg-light py-3 border-top-0">
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted fst-italic">
            * Pola wymagane oznaczone walidacją
          </small>
          <button class="btn btn-primary px-4" @click="saveForm"
          >
            <i class="bx bx-save me-1"></i> Zapisz zmiany
          </button>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import MutableScrollableListView2 from "@/components/widgets/MutableScrollableListView2.vue";
import {log} from "@/state/pinia/log";
import {useSimplepharmAuthModule} from "@/state/pinia/simplepharmAuthModule";
import {mapStores} from "pinia";
import {useCommonAppModule} from "@/state/pinia/commonAppModule";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";
import Swal from "sweetalert2";

export default {
  name: "DrugstoreForm2",
  components: {
    MutableScrollableListView2,
  },
  props: {
    editedDrugstore: Object
  },
  emits: ["onCloseForm","onSave"], // Event likely used by parent to close modal
  data() {
    return {
      drugstore: {
        // Initialize object structure to match original template bindings
        id: null,
        drugstoreClientId: "",
        name: "",
        nip: "",
        address: "", // Street
        city: "",
        zipcode: "",
        email: "",
        phoneNumber: "",
        // Company Data
        companyName: "",
        companyAddress: "", // Textarea in original
        accountNumber: "",
        bankName: "",
        // API Data
        apiBaseUrl: "",
        apiLogin: "",
        apiPassword: "",

        references: [],
        current: false, // Flag from original
      },

      // Validation classes state
      nameInputClass: "form-control",
      nipInputClass: "form-control",
      emailInputClass: "form-control",
      streetInputClass: "form-control",
      zipcodeInputClass: "form-control",
      cityInputClass: "form-control",

      apiPasswordInputClass: "form-control",
      apiLoginInputClass: "form-control",
      apiHostInputClass: "form-control",

      companyNameInputClass: "form-control",
      companyAccountInputClass: "form-control",
      companyBankInputClass: "form-control",
      companyAddressInputClass: "form-control",
      drugstoreNumberInputClass: "form-control",

      // Navigation
      tabActive: 0,
    };
  },
  computed: {
    // Pinia stores access maintained
    simplepharmAuth(){
      return useSimplepharmAuthModule();
    },
    commonModule(){
      return useCommonAppModule();
    },
    lazyloaderModule(){
      return useLazyLoaderModule();
    },
  },
  methods: {
    loadTab(tabIndex) {
      this.tabActive = tabIndex;
    },

    addReference(refItem) {
      // refItem should not contain empty spaces and should not be made of digits only
      console.log("refItem: ", refItem);
      if(refItem.trim() === "") {
        Swal.fire({
          icon: "error",
          title: "Nie można dodać pustej referencji",
          text: "Wprowadź poprawną wartość.",
        }).then(()=>{
          this.$refs.referencesList.removeElement(refItem)
        });
        return;
      }

      if (/^\d+$/.test(refItem.trim())) {
        Swal.fire({
          icon: "error",
          title: "Nie można dodać referencji składającej się tylko z cyfr",
          text: "Wprowadź poprawną wartość.",
        }).then(()=>{
          this.$refs.referencesList.removeElement(refItem)
        });
        return;
      }
      // replace all spaces and _ character wit ''
      const newRefItem = refItem.replace(/\s/g, '').replace(/_/g, '');


      if(newRefItem && !this.drugstore.references.includes(newRefItem)) {
        this.drugstore.references.push(newRefItem);
      }

      console.log("drugstore.references: ", this.drugstore.references);
    },

    removeReference(refItem) {
      this.drugstore.references = this.drugstore.references.filter(item => item !== refItem);
    },

    validate() {
      // Logic refactored for clarity but functionality preserved
      let isValid = true;

      // Reset classes
      const resetClass = "form-control";
      const errorClass = "form-control is-invalid";

      this.nameInputClass = resetClass;
      this.drugstoreNumberInputClass = resetClass;
      this.nipInputClass = resetClass;
      this.streetInputClass = resetClass;
      this.cityInputClass = resetClass;
      this.zipcodeInputClass = resetClass;
      this.emailInputClass = resetClass;
      this.companyNameInputClass = resetClass;
      this.companyAddressInputClass = resetClass;
      this.companyAccountInputClass = resetClass;
      this.companyBankInputClass = resetClass;
      this.apiHostInputClass = resetClass;
      this.apiLoginInputClass = resetClass;
      this.apiPasswordInputClass = resetClass;

      // Basic Data Validation
      if (!this.drugstore.name) {
        this.nameInputClass = errorClass;
        isValid = false;
        this.tabActive = 0; // Switch to tab with error
      }
      if(this.drugstore.id == null || this.drugstore.id === ""){
        // only for new drugstores
        if (!this.drugstore.drugstoreClientId || this.drugstore.drugstoreClientId.length === 0) {
          this.drugstoreNumberInputClass = errorClass;
          isValid = false;
          this.tabActive = 0;
        }
      }


      if (!this.drugstore.nip || this.drugstore.nip.length !== 10) {
        this.nipInputClass = errorClass;
        isValid = false;
        this.tabActive = 0;
      }
      // if (!this.drugstore.address) {
      //   this.streetInputClass = errorClass;
      //   isValid = false;
      //   this.tabActive = 0;
      // }
      // if (!this.drugstore.city) {
      //   this.cityInputClass = errorClass;
      //   isValid = false;
      //   this.tabActive = 0;
      // }
      // if (!this.drugstore.zipcode) {
      //   this.zipcodeInputClass = errorClass;
      //   isValid = false;
      //   this.tabActive = 0;
      // }
      //
      // // Company Data Validation
      // if (!this.drugstore.companyName) {
      //   this.companyNameInputClass = errorClass;
      //   isValid = false;
      //   if(this.tabActive !== 0) this.tabActive = 2;
      // }
      // if (!this.drugstore.companyAddress) {
      //   this.companyAddressInputClass = errorClass;
      //   isValid = false;
      //   if(this.tabActive !== 0) this.tabActive = 2;
      // }

      // API Validation (only if not current/local)
      // if (!this.drugstore.current) {
      //   if (!this.drugstore.apiBaseUrl) {
      //     this.apiHostInputClass = errorClass;
      //     isValid = false;
      //     if(this.tabActive !== 0 && this.tabActive !== 2) this.tabActive = 1;
      //   }
      // }

      return isValid;
    },

    checkForm() {
      return this.validate();
    },
    handleGlobalKeydown(event) {
      if (event.key === "F2") {
        event.preventDefault(); // żeby przeglądarka / system nie zrobiły czegoś swojego
        this.saveForm();
      }
    },
    saveForm() {
      if (this.checkForm()) {
        log.log("Saving drugstore form...", this.drugstore);
        if (this.drugstore.id == null || this.drugstore.id === "") {
          var addRequest = {
            drugstoreObject: this.drugstore,
          };
          this.commonModule.httpAddDrugstore(addRequest).then(() => {
            this.$emit("onSave", this.drugstore);
            this.$emit("onCloseForm", true);
          })
        } else {
          log.log("Updating");
          var editRequest = {
            drugstoreObject: this.drugstore,
            drugstoreId: this.drugstore.id,
          };
          this.commonModule.httpUpdateDrugstore(editRequest).then(() => {
            this.$emit("onSave", this.drugstore);
            this.$emit("onCloseForm", true);
          });
        }

      } else {
        log.log("Form validation failed");
      }
    },

  },
  mounted() {
    window.addEventListener("keydown", this.handleGlobalKeydown);
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.handleGlobalKeydown);
  },
  created() {
    // Deep copy to avoid mutating prop directly
    if (this.editedDrugstore) {
      this.drugstore = JSON.parse(JSON.stringify(this.editedDrugstore));
      // data sanitization to remove 'null' in inputs
      Object.keys(this.drugstore).forEach(key => {
        if (this.drugstore[key] === null || this.drugstore[key] === "null") {
          this.drugstore[key] = "";
        }
      });

    }

    // Ensure references array exists
    if (!this.drugstore.references) {
      this.drugstore.references = [];
    }

    log.log("DrugstoreForm initialized with:");
    log.log(this.drugstore);
  },
};
</script>

<style scoped>
.fs-12 {
  font-size: 12px;
}

/* Subtelna animacja przy przełączaniu tabów */
.animate-fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Stylizacja aktywnych tabów w headerze karty */
.nav-tabs .nav-link.active {
  background-color: #fff;
  border-bottom-color: #fff;
  color: #556ee6; /* Primary color */
  border-top: 2px solid #556ee6;
}

.nav-tabs .nav-link {
  color: #495057;
  border-top: 2px solid transparent;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
  color: #556ee6;
}

/* Dostosowanie textarea */
textarea {
  resize: vertical;
}
</style>